/* eslint-disable no-console */
import * as xml2js from 'xml2js';
import * as utils from '../utils';
import { promises as fsPromises } from 'fs';
import { featureAvailabilityService } from '../commons/featureAvailabilityService';
import {
    isAbortedTest,
    isSkippedTest,
    getFailedTests,
    isFailedTest,
    getFailureEvaluatingCount,
    getSkippedCount,
    getAbortedTests,
} from './reporterUtils';
import type { IReporter } from './IReporter';
import type { RunnerOptions } from '../runOptions';
import type { TestPlanRunner } from '../runners/TestPlanRunner';

type TestPlanResult = Awaited<ReturnType<TestPlanRunner['runAnonymousTestPlan']>>;

class JunitReporter implements IReporter {
    readonly classname: string;
    constructor(private readonly options: RunnerOptions, private readonly branchToUse: string) {
        this.classname = 'testim.io.test';
        if (typeof options.reportFileClassname === 'string') {
            this.classname = options.reportFileClassname;
        }
        if (options.reportFileClassname === true) {
            this.classname = ''; // specific case for passing empty string which makes junit-viewer display test name
        }
    }

    private async createResultsReport(testResults: TestPlanResult) {
        const reportFile = this.options.reportFile;
        const projectId = this.options.project;

        const reportText = await report(this.options.editorUrl!, testResults, projectId, this.branchToUse, this.classname, this.options);
        if (!reportFile) {
            return undefined;
        }
        try {
            await fsPromises.writeFile(reportFile, reportText);
            console.log('JUnit XML file saved to', reportFile);
            return testResults;
        } catch (err) {
            console.error('could not save report file', reportFile, err);
            return testResults;
        }
    }

    public async onAllTestPlansFinished(testPlanResults: TestPlanResult) {
        await this.createResultsReport(testPlanResults);
    }
}

function getPrintName(testResult: TestPlanResult[number]['results'][string]) {
    const testData = testResult.testData || {};
    const testDataNumber = typeof testData.total === 'number' && testData.total !== 1 ? ` - ${testData.index} / ${testData.total} Data set` : '';
    return `${testResult.name}${testDataNumber}`;
}
function getVisitedUrlsList(testResult: TestPlanResult[number]['results'][string]) {
    if (!testResult.visitedUrlsList) {
        console.log('No URLs found:', 'Please contact our support team or remove the --urls flag from the CLI command');
    }
    const visitedUrls = testResult.visitedUrlsList || '';
    return `${visitedUrls}`;
}
function getVisitedUrlsJson(testResult: TestPlanResult[number]['results'][string]) {
    const visitedUrls = testResult.visitedUrlsJson || '';
    return `${visitedUrls}`;
}
async function report(editorUrl: string, testPlanResults: TestPlanResult, projectId: string, branch: string, classname: string, options: RunnerOptions) {
    function createTestCaseObject(testResult: TestPlanResult[number]['results'][string]) {
        const testResultUrl = utils.getTestUrl(editorUrl, projectId, testResult.testId, testResult.resultId, branch);
        interface TestResultObject {
            'system-out': string;
            'visited-urls-list'?: string;
            'visited-urls-json'?: string;
            $: { name: string; classname: string; time: number; ownedBy: string; ownerEmail: string };
            failure?: { $: { message: string } };
            skipped?: {};
        }
        const testResultObject: TestResultObject = {
            $: {
                name: getPrintName(testResult),
                classname,
                time: utils.getDurationSec(testResult.duration),
                ownedBy: testResult.testOwnerName,
                ownerEmail: testResult.testOwnerEmail,
            },
            'system-out': testResultUrl,
        };

        if (isFailedTest(testResult) || isAbortedTest(testResult)) {
            const prefixMessage = `Step Failed: ${testResult.failureReason || testResult.reason}`;
            const message = isFailedTest(testResult) ? `${prefixMessage} More info at: ${testResultUrl}` : prefixMessage;
            testResultObject.failure = {
                $: {
                    message,
                },
            };
        }
        if (isSkippedTest(testResult) && utils.isQuarantineAndNotRemoteRun(testResult, options) && featureAvailabilityService.isTestStatusEnabled) {
            testResultObject.skipped = {};
        }

        if (options.urls) {
            testResultObject['visited-urls-list'] = getVisitedUrlsList(testResult);
            testResultObject['visited-urls-json'] = getVisitedUrlsJson(testResult);
        }

        return testResultObject;
    }

    function createTestSuiteObject(testPlanResult: TestPlanResult[number]) {
        const { results, testPlanName, configName } = testPlanResult;
        const testResults = results || {};
        const suiteName = configName && testPlanName ? `${testPlanName} with config '${configName}'` : testPlanName;
        interface TestSuiteAttributes { name: string; tests: number; failures: number; timestamp: string; skipped?: number; 'failure-evaluating'?: number }
        const testSuiteAttributes: TestSuiteAttributes = {
            name: suiteName || 'selenium run',
            tests: getTestCount(testResults),
            failures: getFailedCount(testResults),
            timestamp: getSuiteTimestamp(testPlanResults),
        };
        if (featureAvailabilityService.isTestStatusEnabled) {
            testSuiteAttributes.skipped = getSkippedCount(testResults, options);
            const failureEvaluatingCount = getFailureEvaluatingCount(testResults);
            testSuiteAttributes.failures -= failureEvaluatingCount;
            testSuiteAttributes['failure-evaluating'] = failureEvaluatingCount;
        }
        return {
            $: testSuiteAttributes,
            testcase: Object.keys(testResults).map(resultId => createTestCaseObject(testResults[resultId])),
        };
    }

    function getSuiteTimestamp(testResults: TestPlanResult[number]['results']) {
        const startTimeArr = Object.keys(testResults).map(resultId => testResults[resultId].startTime);
        const minTestStartTime = Math.min.apply(null, startTimeArr);
        return minTestStartTime ? new Date(minTestStartTime).toISOString() : new Date().toISOString();
    }

    function getTestCount(testResults: TestPlanResult[number]['results']) {
        return Object.keys(testResults).length;
    }

    function getFailedCount(testResults: TestPlanResult[number]['results']) {
        return getFailedTests(testResults).length + getAbortedTests(testResults).length;
    }

    const testResultObject = {
        testsuites: {
            testsuite: testPlanResults.map(testPlanResult => createTestSuiteObject(testPlanResult)),
        },
    };


    try {
        const builder = new xml2js.Builder();
        const jUnitXmlReporter = builder.buildObject(testResultObject);
        return jUnitXmlReporter;
    } catch (err) {
        return createErrorjUnitReporter(err);
    }
}

function createErrorjUnitReporter(err: Error) {
    const builder = new xml2js.Builder();
    const errorJunitObject = {
        testsuites: {
            testsuite: {
                $: {
                    name: 'selenium run',
                    tests: 1,
                    failure: 1,
                    timestamp: Date.now(),
                },
                testcase: {
                    $: {
                        name: 'junit reporter generator failed',
                        classname: 'testim.io.jUnitXmlReporter',
                    },
                    error: {
                        $: {
                            message: err.message,
                        },
                    },
                },
            },
        },
    };
    return builder.buildObject(errorJunitObject);
}

export const Reporter = JunitReporter;
